/**
 * Client-side evaluator for the FieldFilter / RuleFilter shape that
 * WigSearch emits and that de-hub's `filter_to_orm_expr` understands.
 * We run filters in the browser for v0 (500-row payload); when we move
 * to server-side filtering the same query object gets forwarded to
 * POST /api/broker_deals/search unchanged.
 *
 * FieldFilter: { field, operator, value }
 * RuleFilter:  { rules: [FieldFilter|RuleFilter, ...], combinator: "and"|"or" }
 *
 * Supported operators mirror de_api/common.py:
 *   eq, neq, gt, gte, lt, lte, in, nin, exists, nexists, contains, contains!
 *
 * DATES ARE COMPARED BY CALENDAR DAY. The chip bar's DatePicker emits a bare
 * "YYYY-MM-DD" while the rows carry full ISO timestamps, so `eq` could never
 * match and `gt`/`lt` ran both sides through `Number()`, got NaN, and matched
 * nothing at all. When the FILTER value is a bare date literal, both sides
 * are reduced to a local calendar day first — local, because that is the day
 * the grid prints via `toLocaleString()`, and a filter that disagrees with
 * the column beside it is worse than no filter.
 */

/**
 * A bare "YYYY-MM-DD" from the DatePicker. Never a plain number, which makes
 * it a safe discriminator between a date comparison and a numeric one.
 */
const DATE_LITERAL = /^\d{4}-\d{2}-\d{2}/;

/** Local calendar day of a value, as a sortable "YYYY-MM-DD", or null. */
const dayKey = (v) => {
  if (v === null || v === undefined || v === "") return null;
  // A bare date literal is already a local day. Parsing it would drag it
  // through UTC and shift it a day for anyone west of Greenwich.
  if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v.trim())) {
    return v.trim();
  }
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const evalField = (row, filter) => {
  const v = row?.[filter.field];
  const t = filter.value;

  // Day-level comparison, keyed off the FILTER value so a numeric column is
  // never accidentally read as a date.
  if (typeof t === "string" && DATE_LITERAL.test(t.trim())) {
    const a = dayKey(v);
    const b = dayKey(t);
    if (b !== null) {
      switch (filter.operator) {
        case "eq":
          return a === b;
        case "neq":
          return a !== b;
        case "gt":
          return a !== null && a > b;
        case "gte":
          return a !== null && a >= b;
        case "lt":
          return a !== null && a < b;
        case "lte":
          return a !== null && a <= b;
        default:
          // in / nin / contains / exists mean the same thing on a date as on
          // any other string; fall through to the general path.
          break;
      }
    }
  }

  switch (filter.operator) {
    case "eq":
      return v == t; // eslint-disable-line eqeqeq
    case "neq":
      return v != t; // eslint-disable-line eqeqeq
    case "gt":
      return v != null && Number(v) > Number(t);
    case "gte":
      return v != null && Number(v) >= Number(t);
    case "lt":
      return v != null && Number(v) < Number(t);
    case "lte":
      return v != null && Number(v) <= Number(t);
    case "in":
      return Array.isArray(t) && t.some((x) => x == v); // eslint-disable-line eqeqeq
    case "nin":
      return !Array.isArray(t) || !t.some((x) => x == v); // eslint-disable-line eqeqeq
    case "exists":
      return v != null && v !== "";
    case "nexists":
      return v == null || v === "";
    case "contains":
      return String(v ?? "")
        .toLowerCase()
        .includes(String(t ?? "").toLowerCase());
    case "contains!":
      return !String(v ?? "")
        .toLowerCase()
        .includes(String(t ?? "").toLowerCase());
    default:
      return true;
  }
};

const matchQuery = (row, query) => {
  if (!query) return true;
  if (query.operator) return evalField(row, query);
  if (Array.isArray(query.rules)) {
    if (query.rules.length === 0) return true;
    const combinator = query.combinator === "or" ? "or" : "and";
    const results = query.rules.map((r) => matchQuery(row, r));
    return combinator === "or" ? results.some(Boolean) : results.every(Boolean);
  }
  return true;
};

export const filterRows = (rows, query) => {
  if (!rows || rows.length === 0) return rows || [];
  if (!query || !Array.isArray(query.rules) || query.rules.length === 0) {
    return rows;
  }
  return rows.filter((r) => matchQuery(r, query));
};

export default filterRows;
